import type { CustomChartGrammar } from '@erp/viz/custom';
import type { ReportDatasetRow } from '../ReportDataContext';

type Json = Record<string, unknown>;

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Past this many categories colors repeat anyway; the domain is left to Vega. */
const MAX_DOMAIN = 200;
const COLOR_CHANNELS = ['color', 'fill', 'stroke'] as const;
const NESTED = ['layer', 'concat', 'hconcat', 'vconcat'] as const;

/**
 * A column's distinct values, Empty left out, in natural order (2 before
 * 10): a category's place in it is its color. Null when the rows don't have
 * the column or have too many values for a stable color each.
 */
function domainOf(
  rows: readonly ReportDatasetRow[],
  field: string,
): (string | number | boolean)[] | null {
  if (!rows.some((row) => field in row)) return null;
  const seen = new Map<string, string | number | boolean>();
  for (const row of rows) {
    const value = row[field];
    if (value === null || value === undefined || value === '') continue;
    seen.set(String(value), value);
    if (seen.size > MAX_DOMAIN) return null;
  }
  const collator = new Intl.Collator(undefined, { numeric: true });
  return [...seen.entries()]
    .sort(([a], [b]) => collator.compare(a, b))
    .map(([, value]) => value);
}

/**
 * A color channel's definition with its scale's domain fixed to the column's
 * values — or the definition as it was when it isn't a categorical field, the
 * author set a domain, or the rows don't give one.
 */
function channelWithDomain(
  def: unknown,
  rows: readonly ReportDatasetRow[],
): unknown {
  if (!isObject(def) || typeof def.field !== 'string') return def;
  if (def.type !== 'nominal' && def.type !== 'ordinal') return def;
  const scale = isObject(def.scale) ? def.scale : {};
  if ('domain' in scale) return def;
  const domain = domainOf(rows, def.field);
  return domain?.length ? { ...def, scale: { ...scale, domain } } : def;
}

function encodingWithDomains(
  encoding: Json,
  rows: readonly ReportDatasetRow[],
): Json {
  const next = { ...encoding };
  for (const channel of COLOR_CHANNELS) {
    const def = encoding[channel];
    if (def !== undefined) next[channel] = channelWithDomain(def, rows);
  }
  return next;
}

function withEncodingDomains(
  node: Json,
  rows: readonly ReportDatasetRow[],
): Json {
  let next = node;
  if (isObject(node.encoding))
    next = { ...next, encoding: encodingWithDomains(node.encoding, rows) };
  for (const key of NESTED) {
    const children = node[key];
    if (Array.isArray(children))
      next = {
        ...next,
        [key]: children.map((child) =>
          isObject(child) ? withEncodingDomains(child, rows) : child,
        ),
      };
  }
  if (isObject(node.spec))
    next = { ...next, spec: withEncodingDomains(node.spec, rows) };
  return next;
}

function withScaleDomains(spec: Json, rows: readonly ReportDatasetRow[]): Json {
  if (!Array.isArray(spec.scales)) return spec;
  return {
    ...spec,
    scales: spec.scales.map((scale: unknown) => {
      if (!isObject(scale) || scale.type !== 'ordinal') return scale;
      if (!isObject(scale.domain) || typeof scale.domain.field !== 'string')
        return scale;
      const domain = domainOf(rows, scale.domain.field);
      return domain?.length ? { ...scale, domain } : scale;
    }),
  };
}

/**
 * A custom chart's spec with each categorical color keyed to its category,
 * not its position: the color scale's domain is fixed to the column's values
 * in the Report dataset before Report filters (`rows`), so filtering to one
 * category leaves it the color it had — as Tableau and Looker keep a
 * category's color. An author's own domain, a derived field and a
 * quantitative color scale are left alone.
 */
export function stableCategoryDomains(
  grammar: CustomChartGrammar,
  spec: Json,
  rows: readonly ReportDatasetRow[],
): Json {
  return grammar === 'vega'
    ? withScaleDomains(spec, rows)
    : withEncodingDomains(spec, rows);
}
